import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { steps } from '../../../../apps/Antibiogram/config';
import { CategoryStep } from '../../../../apps/Antibiogram/components/steps/CategoryStep';
import { ProtocolStep } from '../../../../apps/Antibiogram/components/steps/ProtocolStep';
import { DiagnosisStep } from '../../../../apps/Antibiogram/components/steps/DiagnosisStep';
import { SensitivitiesStep } from '../../../../apps/Antibiogram/components/SensitivitiesStep';
import { Summary } from '../../../../apps/Antibiogram/components/Summary';
import { buildActivePath, resolveNextStep } from '../../../../apps/Antibiogram/utils';
import '../../../../apps/Antibiogram/Antibiogram.scss';

export default function PreviewPane({ focusedProtocol, focusedDiagnosis, allProtocols, allDrugs, blacklists, categories }) {
  const [currentStepId, setCurrentStepId] = useState('category');
  const [answers, setAnswers] = useState({});
  const [isDone, setIsDone] = useState(false);
  const previousFocusRef = useRef({ protocolId: null, diagnosisId: null });
  const focusedProtocolId = focusedProtocol?.id ?? null;
  const focusedDiagnosisId = focusedDiagnosis?.id ?? null;
  const focusedProtocolCategory = focusedProtocol?.category ?? null;
  const hasFocusedDiagnosis = Boolean(focusedProtocol && focusedDiagnosis);
  const hasFocus = Boolean(focusedProtocol || focusedDiagnosis);

  // Jump to the final preview only when the editor focuses a different item.
  // Live edits still flow through draftConfig below without resetting user navigation.
  useEffect(() => {
    const nextFocus = {
      protocolId: focusedProtocolId == null ? null : String(focusedProtocolId),
      diagnosisId: focusedDiagnosisId == null ? null : String(focusedDiagnosisId)
    };
    const previousFocus = previousFocusRef.current;
    const focusChanged =
      previousFocus.protocolId !== nextFocus.protocolId ||
      previousFocus.diagnosisId !== nextFocus.diagnosisId;

    if (hasFocusedDiagnosis) {
      setAnswers(prev => ({
        ...prev,
        category: focusedProtocolCategory,
        protocol: focusedProtocolId,
        diagnosis: focusedDiagnosisId,
        sensitivities: prev.sensitivities || {} // Keep user's sensitivity selections if any
      }));
      if (focusChanged) {
        setIsDone(true);
      }
    } else if (!hasFocus) {
      setAnswers({});
      setCurrentStepId('category');
      setIsDone(false);
    }

    previousFocusRef.current = nextFocus;
  }, [focusedProtocolId, focusedDiagnosisId, focusedProtocolCategory, hasFocusedDiagnosis, hasFocus]);


  const onSelect = (value) => {
    if (!currentStep) return;
    const stepKey = currentStep.id;
    const newAnswers = {
      ...answers,
      [stepKey]: value,
    };
    setAnswers(newAnswers);
    const nextStepId = resolveNextStep(currentStep, value);
    if (nextStepId) {
      setCurrentStepId(nextStepId);
    }
  };

  const onSensitivitiesDone = (sens) => {
    setAnswers((prev) => ({ ...prev, sensitivities: sens }));
    setIsDone(true);
  };

  const currentStep = steps.find((s) => s.id === currentStepId);
  const path = buildActivePath("category", answers, steps);

  const PROGRESS_SEGMENTS = [
    { key: "category", label: "קטגוריה", stepIds: ["category"] },
    { key: "protocol", label: "פרוטוקול", stepIds: ["protocol"] },
    { key: "diagnosis", label: "אבחנה", stepIds: ["diagnosis"] },
    { key: "sensitivities", label: "רגישויות", stepIds: ["sensitivities"] },
    { key: "recommendations", label: "המלצות", stepIds: [] },
  ];

  const getSegmentState = (segmentIdx) => {
    if (isDone) {
      return segmentIdx < 4 ? "completed" : "active";
    }
    const segment = PROGRESS_SEGMENTS[segmentIdx];
    if (segment.stepIds.includes(currentStepId)) return "active";
    const laterSegment = PROGRESS_SEGMENTS.slice(segmentIdx + 1).some((s) =>
      s.stepIds.includes(currentStepId),
    );
    if (laterSegment) return "completed";
    return "pending";
  };

  const handleSegmentClick = (segmentIdx) => {
    const state = getSegmentState(segmentIdx);
    if (state !== "completed") return;
    const segment = PROGRESS_SEGMENTS[segmentIdx];
    const firstStepId = segment.stepIds.find((id) => path.includes(id));
    if (firstStepId) {
      setCurrentStepId(firstStepId);
      const displayPath = path;
      const idx = displayPath.indexOf(firstStepId);
      const stepsToClear = displayPath.slice(idx);
      const newAnswers = { ...answers };
      stepsToClear.forEach((id) => delete newAnswers[id]);
      setAnswers(newAnswers);
      setIsDone(false);
    }
  };

  const handleBack = () => {
    if (isDone) {
      setIsDone(false);
      setCurrentStepId("sensitivities");
      const newAnswers = { ...answers };
      delete newAnswers.sensitivities;
      setAnswers(newAnswers);
      return;
    }
    const previousStepId = path[path.length - 2];
    if (previousStepId) {
      setCurrentStepId(previousStepId);
      const newAnswers = { ...answers };
      delete newAnswers[previousStepId];
      setAnswers(newAnswers);
    }
  };

  const handleReset = () => {
    setAnswers({});
    setCurrentStepId('category');
    setIsDone(false);
  };

  const getDrugIdsFromDiagnosis = (diagId) => {
    if (!diagId) return [];
    
    // Find the diagnosis in the draftConfig
    let diag = null;
    if (draftConfig.protocols) {
      for (const p of draftConfig.protocols) {
        if (p.diagnoses) {
          const found = p.diagnoses.find(d => String(d.id) === String(diagId));
          if (found) {
            diag = found;
            break;
          }
        }
      }
    }
    
    if (!diag?.treatment) return [];
    const drugIds = new Set();
    const extractFromOptions = (options) => {
      options?.forEach((option) => {
        option.drugs?.forEach((drug) => {
          if (drug.drugId) drugIds.add(drug.drugId);
        });
      });
    };
    extractFromOptions(diag.treatment.firstLine?.options);
    extractFromOptions(diag.treatment.alternative?.options);
    return Array.from(drugIds);
  };

  const draftConfig = {
    protocols: allProtocols ? [...allProtocols] : [],
    blacklists: blacklists || []
  };

  // Replace the focused protocol in the draft config so preview reflects live edits
  if (focusedProtocol && draftConfig.protocols) {
    const index = draftConfig.protocols.findIndex(p => String(p.id) === String(focusedProtocol.id));
    if (index !== -1) {
      draftConfig.protocols[index] = focusedProtocol;
    } else {
      draftConfig.protocols.push(focusedProtocol);
    }
  }

  const progressBar = (
    <div className="progress-bar-container">
      {PROGRESS_SEGMENTS.map((segment, idx) => {
        const state = getSegmentState(idx);
        const prevState = idx > 0 ? getSegmentState(idx - 1) : null;
        return (
          <React.Fragment key={segment.key}>
            {idx > 0 && (
              <div className={`progress-connector ${prevState === "completed" ? "filled" : "pending"}`} />
            )}
            <button
              type="button"
              className={`progress-segment ${state}`}
              onClick={() => handleSegmentClick(idx)}
              disabled={state !== "completed"}
            >
              <div className="progress-circle">{idx + 1}</div>
              <div className="progress-label" style={{ display: 'none' }}>{segment.label}</div>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderContent = () => {
    if (isDone) {
      return (
        <Summary
          config={draftConfig}
          categories={categories}
          drugs={allDrugs}
          steps={steps}
          answers={answers}
          onBack={handleBack}
          handleReset={handleReset}
          progressBar={progressBar}
          customTitle="תצוגה מקדימה"
        />
      );
    }

    const renderStep = () => {
      if (!currentStep) return <div>Step not found</div>;

      switch (currentStep.id) {
        case "category":
          return (
            <CategoryStep
              categories={categories.map((cat) => ({
                id: cat.id,
                label: cat.name,
                imagePath: cat.imagePath,
              }))}
              onSelect={onSelect}
              selectedCategory={answers.category}
            />
          );
        case "protocol":
          return (
            <ProtocolStep
              protocols={draftConfig.protocols}
              onSelect={onSelect}
              answers={answers}
            />
          );
        case "diagnosis":
          return <DiagnosisStep config={draftConfig} onSelect={onSelect} answers={answers} />;
        case "sensitivities":
          return (
            <SensitivitiesStep
              drugs={allDrugs}
              relevantDrugIds={getDrugIdsFromDiagnosis(answers.diagnosis)}
              onDone={onSensitivitiesDone}
            />
          );
        default:
          return <div>Step not found</div>;
      }
    };

    return (
      <div className="antibiogram-container" style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: '20px 0' }}>
        <div className="antibiogram-header">
          <h2>תצוגה מקדימה</h2>
        </div>

        {progressBar}

        <div className="antibiogram-content" style={{ flex: 1 }}>
          <div className="step-content">
            <div className="step-header">
              <button className="back-step-button" onClick={handleBack} style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#103C6E', fontWeight: 'bold' }}>
                חזור →
              </button>
              <h3 className="step-title" style={{ display: 'none' }}>{currentStep?.title || ""}</h3>
            </div>
            {renderStep()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Box className="mobile-preview" sx={{ width: '375px', height: '812px', maxHeight: 'calc(100vh - 40px)', bgcolor: '#fff', borderRadius: '36px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', border: '12px solid #1a1a1a', position: 'relative' }} dir="rtl">
      {/* Mobile Notch simulation */}
      <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '120px', height: '24px', bgcolor: '#1a1a1a', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px', zIndex: 100 }} />
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {renderContent()}
      </Box>
    </Box>
  );
}

const idPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

const protocolPropType = PropTypes.shape({
  id: idPropType,
  category: idPropType,
  diagnoses: PropTypes.array
});

PreviewPane.propTypes = {
  focusedProtocol: protocolPropType,
  focusedDiagnosis: PropTypes.shape({
    id: idPropType
  }),
  allProtocols: PropTypes.arrayOf(protocolPropType),
  allDrugs: PropTypes.array.isRequired,
  blacklists: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: idPropType,
    name: PropTypes.string,
    imagePath: PropTypes.string
  })).isRequired
};
